import type { Compania } from '../../services/companiaService';
import type { FacturaVenta, FacturaVentaDetalle } from '../../services/facturaVentaService';
import { generarAfipQrUrl } from './afipQrUrl';

export type TicketPrintOptions = {
  compania: Compania;
  incluirQr?: boolean;
};

export type TicketPrintLine =
  | { type: 'center'; text: string }
  | { type: 'left'; text: string }
  | { type: 'row'; left: string; right: string; bold?: boolean }
  | { type: 'rule' }
  | { type: 'blank' };

function numFactura(factura: FacturaVenta & Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const v = Number(factura[key]);
    if (Number.isFinite(v)) return v;
  }
  return 0;
}

function subtotalDetalle(det: FacturaVentaDetalle): number {
  if (det.subtotalFinal != null && det.subtotalFinal > 0) return det.subtotalFinal;
  if (det.subtotal != null && det.subtotal > 0) return det.subtotal;
  return (Number(det.precioUnitario) || 0) * (Number(det.cantidad) || 0);
}

function resolveTicketTotales(factura: FacturaVenta) {
  const raw = factura as FacturaVenta & Record<string, unknown>;
  const letra = String(factura.letra ?? '').trim().toUpperCase();
  const esLetraA = letra === 'A';

  const subtotal = numFactura(raw, 'subtotal', 'Subtotal');
  const iva = numFactura(raw, 'ivainscripto', 'Ivainscripto');
  const descuento = numFactura(raw, 'descuento', 'Descuento');
  const exento = numFactura(raw, 'exento', 'Exento');
  let importeTotal = numFactura(raw, 'importeTotal', 'ImporteTotal');

  if (importeTotal <= 0 && factura.detalles?.length) {
    importeTotal = factura.detalles.reduce((sum, det) => sum + subtotalDetalle(det), 0);
  }

  let subtotalDisplay = esLetraA ? subtotal : subtotal + iva;
  if (!esLetraA && subtotalDisplay <= 0) subtotalDisplay = importeTotal;
  if (subtotalDisplay <= 0 && subtotal > 0) subtotalDisplay = subtotal;
  if (subtotalDisplay <= 0) {
    const sinIva = numFactura(raw, 'importeSinIva', 'ImporteSinIva');
    if (sinIva > 0) subtotalDisplay = sinIva;
  }

  if (importeTotal <= 0 && subtotalDisplay > 0) {
    importeTotal = esLetraA ? subtotalDisplay + iva - descuento : subtotalDisplay;
  }

  return { esLetraA, subtotalDisplay, iva, descuento, exento, importeTotal };
}

function formatMoneda(valor: number | null | undefined): string {
  const n = Number(valor) || 0;
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha: string | Date | undefined): string {
  if (!fecha) return '';
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return String(fecha);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function comprobanteLabel(factura: FacturaVenta): string {
  const letra = String(factura.letra ?? '').trim().toUpperCase();
  const pto = String(factura.nroSuc ?? '').padStart(4, '0');
  const nro = String(factura.nroComprobante ?? '').padStart(8, '0');
  return `${letra} ${pto}-${nro}`;
}

export function buildFacturaVentaTicketPrintLines(
  factura: FacturaVenta,
  options: TicketPrintOptions,
): TicketPrintLine[] {
  const { compania, incluirQr = true } = options;
  const lines: TicketPrintLine[] = [];
  const totales = resolveTicketTotales(factura);

  lines.push({ type: 'center', text: String(compania.nombre ?? '') });

  const domicilio = [compania.domicilio, compania.localidad, compania.provincia].filter(Boolean).join(', ');
  if (domicilio) lines.push({ type: 'center', text: domicilio });
  if (compania.cuit) lines.push({ type: 'center', text: `CUIT: ${compania.cuit}` });
  if (compania.condIva) lines.push({ type: 'center', text: String(compania.condIva) });

  lines.push({ type: 'rule' });
  lines.push({ type: 'center', text: 'FACTURA' });
  lines.push({ type: 'center', text: comprobanteLabel(factura) });
  lines.push({ type: 'rule' });

  lines.push({ type: 'row', left: 'Fecha', right: formatFecha(factura.fechaEmision) });
  if (factura.nombre) {
    lines.push({ type: 'left', text: `Cliente: ${String(factura.nombre).trim()}` });
  }
  if (factura.cuit) lines.push({ type: 'left', text: `CUIT/DNI: ${factura.cuit}` });

  lines.push({ type: 'rule' });
  for (const det of factura.detalles ?? []) {
    const desc = String(det.descripcionArticulo ?? det.codigoArticulo ?? '').trim();
    const cant = Number(det.cantidad) || 0;
    const um = String(det.um ?? '').trim();
    const cantStr = cant % 1 === 0 ? String(cant) : cant.toFixed(2);
    lines.push({ type: 'left', text: `${cantStr}${um ? ` ${um}` : ''} x ${desc}` });
    lines.push({ type: 'row', left: '', right: `$${formatMoneda(subtotalDetalle(det))}` });
  }
  lines.push({ type: 'rule' });

  lines.push({ type: 'row', left: 'Subtotal', right: `$${formatMoneda(totales.subtotalDisplay)}` });
  if (totales.descuento > 0) {
    lines.push({ type: 'row', left: 'Descuento', right: `$${formatMoneda(totales.descuento)}` });
  }
  if (totales.exento > 0) {
    lines.push({ type: 'row', left: 'Exento', right: `$${formatMoneda(totales.exento)}` });
  }
  if (totales.esLetraA && totales.iva > 0) {
    lines.push({ type: 'row', left: 'IVA Discriminado', right: `$${formatMoneda(totales.iva)}` });
  }
  lines.push({
    type: 'row',
    left: 'TOTAL',
    right: `$${formatMoneda(totales.importeTotal)}`,
    bold: true,
  });

  if (factura.caeNro) {
    lines.push({ type: 'rule' });
    lines.push({ type: 'row', left: 'CAE', right: String(factura.caeNro) });
    if (factura.caeVencimiento) {
      lines.push({ type: 'row', left: 'Vto CAE', right: formatFecha(factura.caeVencimiento) });
    }
  }

  if (incluirQr && factura.caeNro) {
    const qrUrl = generarAfipQrUrl(factura, compania);
    if (qrUrl) {
      lines.push({ type: 'blank' });
      lines.push({ type: 'center', text: 'Comprobante autorizado' });
      lines.push({ type: 'left', text: qrUrl });
    }
  }

  lines.push({ type: 'blank' });
  return lines;
}
